import { DecryptionFailedError } from "../ports/cipher.js";
import { asKey, wipe } from "./keys.js";
export class NoSuchWrapError extends Error {
    constructor(holder) {
        super(`в акаунті немає обгортки «${holder}»`);
        this.name = "NoSuchWrapError";
    }
}
export function wrapFor(record, holder) {
    const found = record.wraps.find((w) => w.holder === holder);
    if (!found)
        throw new NoSuchWrapError(holder);
    return found;
}
/**
 * Запамʼятати ключ даних на цьому пристрої — обгорнути його ключем
 * пристрою (алгоритм 3, невитягуваний ключ у сховищі браузера).
 *
 * Заради чого: без цього кожне відкриття вкладки коштувало б секунди
 * Argon2id і вимагало пароля. З цим пароль потрібен лише при першому
 * вході на пристрої, а вкрадена копія бази без сховища ключів браузера
 * лишається шифротекстом.
 *
 * Байти віддаються назовні як є — це блоб для локального сховища, не для
 * сервера. Ідентифікатор акаунта всередині, щоб чужий блоб не розгорнувся
 * мовчки в чужий ключ.
 */
export async function rememberOnDevice(cipher, keys) {
    const id = new TextEncoder().encode(keys.accountId);
    if (id.length > 255)
        throw new Error("ідентифікатор акаунта задовгий");
    const payload = new Uint8Array(1 + id.length + keys.dek.length);
    payload[0] = id.length;
    payload.set(id, 1);
    payload.set(keys.dek, 1 + id.length);
    try {
        return await cipher.seal(payload);
    }
    finally {
        wipe(payload);
    }
}
export async function recallOnDevice(cipher, accountId, blob) {
    const payload = await cipher.open(blob);
    const idLength = payload[0] ?? 0;
    const id = new TextDecoder().decode(payload.subarray(1, 1 + idLength));
    if (id !== accountId) {
        throw new DecryptionFailedError(`збережений ключ належить іншому акаунту (${id})`);
    }
    return { accountId, dek: asKey(payload.slice(1 + idLength)) };
}
/**
 * Base64 туди й назад. Експортовані, бо той самий формат потрібен
 * транспорту синхронізації: дельта їде на сервер полем JSON.
 *
 * Третьої копії бути не має — вона розійшлася б із цією на першому
 * ж символі, і розбіжність було б видно лише як «дельта не
 * розшифровується».
 */
export function toBase64(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
export function fromBase64(text) {
    const binary = atob(text);
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}
/**
 * Запис акаунта у вигляді, придатному для передачі на сервер.
 *
 * Окрема функція, а не `JSON.stringify` на місці виклику, саме заради
 * перевірок нижче: дві обгортки цим шляхом не їдуть ніколи, і помилитися
 * тут має бути неможливо.
 *
 * **Пристрій** — бо він device-local за [ADR-0016] і на сервері йому
 * немає що робити.
 *
 * **Google** — з протилежної причини: він на сервер саме що **їде**, але
 * не сюди. Обгортка ескроу лягає у власну таблицю разом із ключем, яким
 * відкривається ([ADR-0036](../../../../docs/adr/0036-google-sign-in.md),
 * варіант Б), — і це не формальність розкладки. Те, чим сервер **може**
 * прочитати архів, мусить бути видно в схемі одним рядком, а не
 * розчинене серед обгорток, яких він відкрити не може.
 *
 * Якби вона проїхала тут, сервер відкинув би її обмеженням
 * `holder IN ('passphrase','recovery')` — але вже в рантаймі й з
 * повідомленням про порушення CHECK, тобто в найгіршому місці й у
 * найгіршій формі.
 */
export function encodeAccountRecord(record) {
    const local = record.wraps.find((w) => w.holder === "device");
    if (local)
        throw new Error("обгортка пристрою не передається на сервер");
    const escrow = record.wraps.find((w) => w.holder === "google");
    if (escrow)
        throw new Error("обгортка ескроу передається окремим маршрутом, не із записом акаунта");
    return JSON.stringify({
        v: 1,
        accountId: record.accountId,
        kdf: { ...record.kdf, salt: toBase64(record.kdf.salt) },
        wraps: record.wraps.map((w) => ({ holder: w.holder, sealed: toBase64(w.sealed) })),
    });
}
export function decodeAccountRecord(text) {
    const wire = JSON.parse(text);
    if (wire.v !== 1)
        throw new Error(`запис акаунта версії ${wire.v} тут не читається`);
    return {
        accountId: wire.accountId,
        kdf: { ...wire.kdf, salt: fromBase64(wire.kdf.salt) },
        wraps: wire.wraps.map((w) => ({ holder: w.holder, sealed: fromBase64(w.sealed) })),
    };
}
//# sourceMappingURL=record.js.map